import React, { useState } from 'react';
import { Modal, ScrollView, Pressable, KeyboardAvoidingView, Platform } from 'react-native';
import { YStack, XStack, H4, SizableText, Button, Input, TextArea } from 'tamagui';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';

interface CreateThreadModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    content: string;
    tags?: string[];
    isSticky?: boolean;
  }) => void;
  loading?: boolean;
  topicTitle?: string;
  currentUserId?: string;
  canModerate?: boolean;
}

const CreateThreadModal: React.FC<CreateThreadModalProps> = ({
  visible,
  onClose,
  onSubmit,
  loading = false,
  topicTitle,
  canModerate = false
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [isSticky, setIsSticky] = useState(false);
  const theme = themeSelector();

  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    modalBackground: theme ? '#f8fafc' : '#374151',
    cardBackground: theme ? '#ffffff' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    overlay: 'rgba(0, 0, 0, 0.5)',
    accent: '#3b82f6',
    warning: '#f59e0b',
  };

  const handleSubmit = () => {
    if (!title.trim() || !content.trim()) return;

    const tagArray = tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    onSubmit({
      title: title.trim(),
      content: content.trim(),
      tags: tagArray.length > 0 ? tagArray : undefined,
      isSticky: canModerate ? isSticky : false,
    });

    // Reset form
    setTitle('');
    setContent('');
    setTags('');
    setIsSticky(false);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset form on close
      setTitle('');
      setContent('');
      setTags('');
      setIsSticky(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <Pressable
          style={{
            flex: 1,
            backgroundColor: colors.overlay,
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
          }}
          onPress={handleClose}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              backgroundColor: colors.background,
              borderRadius: 16,
              width: '100%',
              maxWidth: 600,
              maxHeight: '90%',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 8,
            }}
          >
            <Animatable.View animation="slideInUp" duration={300}>
              {/* Header */}
              <XStack
                padding="$4"
                alignItems="center"
                justifyContent="space-between"
                borderBottomWidth={1}
                borderBottomColor={colors.border}
              >
                <YStack gap="$1">
                  <H4 color={colors.text}>Start New Discussion</H4>
                  {topicTitle && (
                    <SizableText color={colors.textSecondary} size="$2">
                      in {topicTitle}
                    </SizableText>
                  )}
                </YStack>
                <Pressable onPress={handleClose} disabled={loading}>
                  <MaterialIcons
                    name="close"
                    size={24}
                    color={colors.textSecondary}
                  />
                </Pressable>
              </XStack>

              <ScrollView
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ padding: 16 }}
              >
                <YStack gap="$4">
                  {/* Title */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600">
                      Discussion Title *
                    </SizableText>
                    <Input
                      placeholder="What would you like to discuss?"
                      value={title}
                      onChangeText={setTitle}
                      backgroundColor={colors.modalBackground}
                      borderColor={colors.border}
                      color={colors.text}
                      maxLength={150}
                    />
                    <SizableText color={colors.textSecondary} size="$2">
                      {title.length}/150 characters
                    </SizableText>
                  </YStack>

                  {/* Content */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600">
                      Content *
                    </SizableText>
                    <TextArea
                      placeholder="Share your thoughts, ask questions, or start a conversation..."
                      value={content}
                      onChangeText={setContent}
                      backgroundColor={colors.modalBackground}
                      borderColor={colors.border}
                      color={colors.text}
                      minHeight={120}
                      maxLength={2000}
                      textAlignVertical="top"
                    />
                    <SizableText color={colors.textSecondary} size="$2">
                      {content.length}/2000 characters
                    </SizableText>
                  </YStack>

                  {/* Tags */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600">
                      Tags (Optional)
                    </SizableText>
                    <Input
                      placeholder="Add tags to help others find your discussion..."
                      value={tags}
                      onChangeText={setTags}
                      backgroundColor={colors.modalBackground}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                    <SizableText color={colors.textSecondary} size="$2">
                      Separate multiple tags with commas (e.g., question, help, bug, feature)
                    </SizableText>
                  </YStack>

                  {/* Moderator Options */}
                  {canModerate && (
                    <YStack gap="$2">
                      <SizableText color={colors.text} fontWeight="600">
                        Moderator Options
                      </SizableText>
                      <Pressable
                        onPress={() => setIsSticky(!isSticky)}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          backgroundColor: colors.modalBackground,
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: colors.border,
                        }}
                      >
                        <XStack alignItems="center" gap="$3" flex={1}>
                          <MaterialIcons
                            name={isSticky ? 'check-box' : 'check-box-outline-blank'}
                            size={24}
                            color={isSticky ? colors.warning : colors.textSecondary}
                          />
                          <YStack flex={1} gap="$1">
                            <SizableText color={colors.text} fontWeight="500">
                              Pin to Top (Sticky)
                            </SizableText>
                            <SizableText color={colors.textSecondary} size="$2">
                              Keep this discussion at the top of the list
                            </SizableText>
                          </YStack>
                        </XStack>
                      </Pressable>
                    </YStack>
                  )}

                  {/* Preview */}
                  {title && content && (
                    <YStack gap="$2">
                      <SizableText color={colors.text} fontWeight="600">
                        Preview
                      </SizableText>
                      <YStack
                        padding="$4"
                        backgroundColor={colors.modalBackground}
                        borderRadius="$3"
                        borderWidth={1}
                        borderColor={colors.border}
                        gap="$3"
                      >
                        <XStack alignItems="flex-start" gap="$3">
                          <YStack
                            width={36}
                            height={36}
                            backgroundColor={`${colors.accent}15`}
                            borderRadius={18}
                            justifyContent="center"
                            alignItems="center"
                          >
                            <MaterialCommunityIcons
                              name="message-text"
                              size={18}
                              color={colors.accent}
                            />
                          </YStack>
                          <YStack flex={1} gap="$2">
                            <XStack alignItems="center" gap="$2">
                              {isSticky && (
                                <MaterialIcons
                                  name="push-pin"
                                  size={16}
                                  color={colors.warning}
                                />
                              )}
                              <SizableText color={colors.text} fontWeight="600">
                                {title}
                              </SizableText>
                            </XStack>
                            <SizableText 
                              color={colors.textSecondary} 
                              size="$2"
                              numberOfLines={3}
                            >
                              {content}
                            </SizableText>
                            {tags && (
                              <XStack gap="$1" flexWrap="wrap" marginTop="$1">
                                {tags.split(',').map((tag, index) => (
                                  <YStack
                                    key={index}
                                    backgroundColor={`${colors.accent}20`}
                                    paddingHorizontal={6}
                                    paddingVertical={2}
                                    borderRadius={4}
                                  >
                                    <SizableText color={colors.accent} size="$1">
                                      {tag.trim()}
                                    </SizableText>
                                  </YStack>
                                ))}
                              </XStack>
                            )}
                          </YStack>
                        </XStack>
                      </YStack>
                    </YStack>
                  )}

                  {/* Guidelines */}
                  <YStack 
                    padding="$3" 
                    backgroundColor={`${colors.accent}10`}
                    borderRadius="$2"
                    gap="$2"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialCommunityIcons
                        name="information"
                        size={16}
                        color={colors.accent}
                      />
                      <SizableText color={colors.accent} fontWeight="600" size="$2">
                        Community Guidelines
                      </SizableText>
                    </XStack>
                    <SizableText color={colors.textSecondary} size="$2" lineHeight={16}>
                      • Be respectful and constructive in your discussions{'\n'}
                      • Use clear, descriptive titles{'\n'}
                      • Search for existing discussions before creating new ones{'\n'}
                      • Add relevant tags to help others find your content
                    </SizableText>
                  </YStack>
                </YStack>
              </ScrollView>

              {/* Footer */}
              <XStack
                padding="$4"
                justifyContent="space-between"
                borderTopWidth={1}
                borderTopColor={colors.border}
                gap="$2"
              >
                <Button
                  flex={1}
                  variant="outlined"
                  borderColor={colors.border}
                  color={colors.textSecondary}
                  onPress={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  backgroundColor={colors.accent}
                  color="white"
                  onPress={handleSubmit}
                  disabled={!title.trim() || !content.trim() || loading}
                  opacity={(!title.trim() || !content.trim() || loading) ? 0.5 : 1}
                  icon={loading ? (
                    <MaterialCommunityIcons name="loading" size={18} color="white" />
                  ) : (
                    <MaterialCommunityIcons name="send" size={18} color="white" />
                  )}
                >
                  {loading ? 'Creating...' : 'Start Discussion'}
                </Button>
              </XStack>
            </Animatable.View>
          </Pressable>
        </Pressable>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default CreateThreadModal;
